import { useState } from 'react';

import { cn } from '../cn';
import type { InputProps } from './Input';
import Input from './Input';
import Button from './Button';
import ButtonGroup from './ButtonGroup';
import { General } from '../StrokeIcons';

/**
 * Password field: a text/password Input plus a show/hide toggle, grouped in a
 * ButtonGroup.
 *
 * The group is `overflow-hidden` with a fixed height, so we must NOT let the
 * inner Input render its own error UI: (1) its error message would be clipped
 * inside the group, and (2) its error state reddens the icon/placeholder/text,
 * which makes an *empty* field look like it's pre-filled with an error. So we
 * intercept `error` here — the group only takes the error *border*, and the
 * message renders below the group (matching the base Input's error layout),
 * where it can't be clipped.
 */
export default function PasswordInput({ className, size, error, ...props }: Omit<InputProps, 'type'>) {
  const [showPassword, setShowPassword] = useState(false);
  const hasError = error != null && error !== '';

  return (
    <div className="flex flex-col">
      <ButtonGroup size={size} className={cn(className, hasError && 'border-fair-pink-300! dark:border-fair-pink-900!')}>
        <Input
          type={showPassword ? 'text' : 'password'}
          className={cn(
            className,
            'flex-1 border-none! bg-transparent! focus-within:bg-transparent! hover:bg-transparent! outline-hidden!',
          )}
          {...props}
        />
        <Button
          type="button"
          icon={showPassword ? <General.EyeOff /> : <General.Eye />}
          onClick={() => setShowPassword(!showPassword)}
          tooltip={showPassword ? 'Hide' : 'Show'}
          text
          className={cn(className, 'bg-transparent! hover:bg-transparent!')}
        />
      </ButtonGroup>
      {hasError && (
        <div className="text-sm leading-5 font-normal text-fair-pink-600 dark:text-fair-pink-400 px-3 py-0.5">
          {error}
        </div>
      )}
    </div>
  );
}
